import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/module.d-CnjH8Dlt';
import { Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { catchError, map, shareReplay, startWith, take } from 'rxjs/operators';
import { Territory } from '../../../../core/classes/territory';
import { GenApiKeyRequestStatus } from '../../../../generated/serverModels/GenApiKeyRequestStatus';
import { GenStateAccessRequest } from '../../../../generated/serverModels/GenStateAccessRequest';
import { LocationService } from '../../../../services/location.service';
import { selectTerritories } from '../../../../state/location/location.reducer';

@Component({
  selector: 'tgrid-approved-states-modal',
  standalone: true,
  imports: [
    MatDialogContent,
    MatDialogActions,
    MatAutocompleteModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    MatTooltipModule,
    MatMenuModule,
    MatSnackBarModule,
    ReactiveFormsModule,
    MatDivider,
],
  templateUrl: './approved-states-modal.component.html',
  styleUrl: './approved-states-modal.component.scss',
})
export class ApprovedStatesModalComponent implements OnInit {
  private ref = inject(MatDialogRef<ApprovedStatesModalComponent>);
  private locationService = inject(LocationService);
  private snackBar = inject(MatSnackBar);
  private data: { userId: number } = inject(MAT_DIALOG_DATA);
  private territories$ = inject(Store).select(selectTerritories);

  protected filterControl = new FormControl<string>('');
  protected denialReasonControl = new FormControl('');

  protected isLoading = signal(true);
  protected loadFailed = signal(false);
  protected isSaving = signal(false);
  protected saveError = signal(false);

  private selectedStates$ = new BehaviorSubject<Territory[]>([]);
  protected selectedStates: Observable<Territory[]> =
    this.selectedStates$.asObservable();

  protected filteredTerritories$: Observable<Territory[]>;
  protected stateRequest = signal<GenStateAccessRequest[]>([]);

  constructor() {
    this.filteredTerritories$ = combineLatest([
      this.filterControl.valueChanges.pipe(startWith('')),
      this.territories$,
      this.selectedStates$,
    ]).pipe(
      map(([filterValue, territories, selected]) => {
        const filter = (
          typeof filterValue === 'string' ? filterValue : ''
        ).toLowerCase();
        return territories.filter(
          (t) =>
            !selected.some((s) => s.id === t.id) &&
            t.name.toLowerCase().includes(filter),
        );
      }),
      takeUntilDestroyed(),
      shareReplay(1),
    );
  }

  ngOnInit(): void {
    this.loadApprovedStates();
    this.loadRequestedStates();
  }

  private loadRequestedStates(): void {
    this.locationService.getStateAccessRequests(this.data.userId).subscribe({
      next: (requests) => this.stateRequest.set(requests.filter((r: GenStateAccessRequest) => r.status === GenApiKeyRequestStatus.PENDING)),
    });
  }

  private loadApprovedStates(): void {
    this.locationService.getApprovedStates(this.data.userId).subscribe({
      next: (states) => {
        this.selectedStates$.next(states);
        this.isLoading.set(false);
      },
      error: () => {
        this.loadFailed.set(true);
        this.isLoading.set(false);
      },
    });
  }

  protected addState(event: MatAutocompleteSelectedEvent): void {
    const territory: Territory = event.option.value;
    this.selectedStates$.next([...this.selectedStates$.getValue(), territory]);
    this.filterControl.setValue('', { emitEvent: true });
  }

  protected removeState(territory: Territory): void {
    this.selectedStates$.next(
      this.selectedStates$.getValue().filter((s) => s.id !== territory.id),
    );
  }

  protected submit(): void {
    this.isSaving.set(true);
    this.saveError.set(false);
    const abbreviations = this.selectedStates$
      .getValue()
      .map((s) => s.abbreviation);
    this.locationService
      .setApprovedStates(this.data.userId, abbreviations)
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.ref.close(true);
        },
        error: () => {
          this.isSaving.set(false);
          this.saveError.set(true);
        },
      });
  }

  protected cancel(): void {
    this.ref.close();
  }

  protected openDenyMenu(): void {
    this.denialReasonControl.reset('');
  }

  protected displayFn(territory: Territory): string {
    return territory?.name ?? '';
  }
  protected denyRequest(request: GenStateAccessRequest, reason: string) {
    this.locationService.denyStateAccessRequest(request.id, reason).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to reject request', error);
        this.snackBar.open('Failed to reject request', 'Close', { duration: 5000 });
        return [];
      }),
    ).subscribe(() => {
      request.status = GenApiKeyRequestStatus.DENIED;
      this.snackBar.open('Request has been rejected', 'Close', { duration: 5000 });
      this.loadRequestedStates();
    });
  }
  protected approveRequest(request: GenStateAccessRequest): void {
    this.locationService.approveStateAccessRequest(request.id)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Failed to approve request', error);
          this.snackBar.open('Failed to approve request, can still manually add state', 'Close', { duration: 5000 });
          return [];
        }),
      )
      .subscribe(() => this.addApprovedState(request));
  }

  private addApprovedState(request: GenStateAccessRequest): void {
    this.territories$.pipe(take(1)).subscribe((territories: Territory[]) => {
      const state = territories.find(s => s.id === request.stateId);
      if (state) {
        this.selectedStates$.next([...this.selectedStates$.getValue(), state]);
        this.loadRequestedStates();
        this.loadApprovedStates();
      }
    });
  }
}
